import { type Dispatch, type JSX, memo, type SetStateAction } from 'react';

import { categoryGroups } from '@cx-modules/constants';
import { type Repub } from '@cx-modules/types';
import { extractTeamsFromSubeventName } from '@cx-modules/utils';

import { useMatchesCouponContext } from '../../hooks';
import { type Subevent } from '../../types';
import { AllOddsLink } from '../AllOddsLink';
import { BestOddsButtons } from '../BestOddsButtons';
import { BetLink } from '../BetLink';
import { TeamInfo } from '../Team';
import { Time } from '../Time';

import { RowContent } from './style';

export interface EventRowContentProps {
  betLocation?: string;
  matchStatsEnabled: boolean;
  matchStatsVisible: boolean;
  pageTitle?: string;
  repub: Repub;
  requestUrl: string;
  setGroupTitleLabelsMarginRight: Dispatch<SetStateAction<number>>;
  showTeamBadges: boolean;
  subevent: Subevent;
}

const EventRowContent = ({
  betLocation,
  pageTitle,
  repub,
  requestUrl,
  setGroupTitleLabelsMarginRight,
  showTeamBadges,
  subevent,
}: EventRowContentProps): JSX.Element => {
  const { awayTeam, homeTeam, id, inRunning, startTime, urlPath = '' } = subevent;
  const subeventNameHasAtPattern = subevent.name.includes(' at ') && !subevent.name.includes(' v|vs ');
  const homeTeamName = homeTeam?.name || '';
  const awayTeamName = awayTeam?.name || '';

  const teamHierarchyNames = extractTeamsFromSubeventName(subevent.name);
  const categoryGroup = categoryGroups.find(cg => cg.id === subevent.categoryGroupId);
  const showMoreOddsButton = useMatchesCouponContext(state => state.showMoreOddsButton);

  return (
    <RowContent data-testid="event-row">
      <Time inRunning={inRunning} repub={repub} startTime={startTime} />
      <BetLink
        awayTeamName={awayTeamName}
        homeTeamName={homeTeamName}
        requestUrl={requestUrl}
        subeventId={id}
        url={urlPath}
      >
        {subeventNameHasAtPattern ? (
          <>
            <TeamInfo
              categoryGroup={categoryGroup}
              hierarchyName={teamHierarchyNames.away}
              showTeamBadges={showTeamBadges}
              team={awayTeam}
            />
            <TeamInfo
              categoryGroup={categoryGroup}
              hierarchyName={teamHierarchyNames.home}
              home={subeventNameHasAtPattern}
              showTeamBadges={showTeamBadges}
              team={homeTeam}
            />
          </>
        ) : (
          <>
            <TeamInfo
              categoryGroup={categoryGroup}
              hierarchyName={teamHierarchyNames.home}
              showTeamBadges={showTeamBadges}
              team={homeTeam}
            />
            <TeamInfo
              categoryGroup={categoryGroup}
              hierarchyName={teamHierarchyNames.away}
              showTeamBadges={showTeamBadges}
              team={awayTeam}
            />
          </>
        )}
      </BetLink>
      <BestOddsButtons betLocation={betLocation} pageTitle={pageTitle} subevent={subevent} />
      {showMoreOddsButton && (
        <AllOddsLink
          requestUrl={requestUrl}
          setGroupTitleLabelsMarginRight={setGroupTitleLabelsMarginRight}
          subeventId={id}
          url={urlPath}
        />
      )}
    </RowContent>
  );
};

export default memo(EventRowContent);
